"use client";

import { ctaClick } from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { formatBackgroundStyle } from "@/utils/styles/format";
import type { IProgramCardsCategory } from "@/types/page";
import { Button } from "@/components/atoms/Button";
import { Dialog, DialogOverlay, DialogPortal } from "@/components/atoms/Dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import type { CSSProperties } from "react";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import * as z from "@/lib/propSchema";
import Image from "next/image";
import { LeftArrow, RightArrow } from "@/assets/Arrow";
import { LEAD_DROP_VARIANTS } from "../../../constants";
import RevealOnScroll from "@/components/atoms/RevealOnScroll";

export const PROGRAM_CARDS_2_FONT_FAMILY_VARIANT_1_MAP = {
  section: "var(--font-poppins)",
  preTitle: "var(--font-poppins)",
  primaryHeading: "var(--font-poppins)",
  secondaryHeading: "var(--font-poppins)",
  body: "var(--font-poppins)",
  cta: "var(--font-poppins)",
  learners: "var(--font-poppins)",
  rating: "var(--font-poppins)",
  sectionPreTitleClassName:
    "text-[24px] min-[1080px]:text-[28px] min-[1230px]:text-[30px]",
  sectionTitleClassName:
    "text-[40px] min-[1080px]:text-[44px] min-[1230px]:text-[54px] xl:text-[64px] tracking-[1.2px] min-[1080px]:tracking-[1.32px] min-[1230px]:tracking-[1.62px] xl:tracking-[1.92px]",
} as const;


export const PROGRAM_CARDS_2_FONT_FAMILY_VARIANT_2_MAP = {
  section: "var(--font-google-sans)",
  preTitle: "var(--font-google-sans)",
  primaryHeading: "var(--font-merriweather)",
  secondaryHeading: "var(--font-google-sans)",
  body: "var(--font-google-sans)",
  cta: "var(--font-google-sans)",
  learners: "var(--font-google-sans)",
  rating: "var(--font-google-sans)",
  sectionPreTitleClassName:
    "text-[24px] min-[1080px]:text-[28px] min-[1230px]:text-[30px]",
  sectionTitleClassName:
    "text-[40px] min-[1080px]:text-[44px] min-[1230px]:text-[54px] xl:text-[64px] tracking-[1.2px] min-[1080px]:tracking-[1.32px] min-[1230px]:tracking-[1.62px] xl:tracking-[1.92px]",
} as const;

export type ProgramCards2FontFamilyMap =
  | typeof PROGRAM_CARDS_2_FONT_FAMILY_VARIANT_1_MAP
  | typeof PROGRAM_CARDS_2_FONT_FAMILY_VARIANT_2_MAP;

const DurationIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M8 2V5" stroke="#292D32" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M16 2V5" stroke="#292D32" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M3.5 9.08997H20.5" stroke="#292D32" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M21 8.5V17C21 20 19.5 22 16 22H8C4.5 22 3 20 3 17V8.5C3 5.5 4.5 3.5 8 3.5H16C19.5 3.5 21 5.5 21 8.5Z" stroke="#292D32" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M15.6937 13.7H15.7027" stroke="#292D32" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M15.6937 16.7H15.7027" stroke="#292D32" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M11.9945 13.7H12.0035" stroke="#292D32" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M11.9945 16.7H12.0035" stroke="#292D32" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M8.29529 13.7H8.30427" stroke="#292D32" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M8.29529 16.7H8.30427" stroke="#292D32" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  );
};

const FormatIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M22 11.89V12.78C22 16.34 21.11 17.22 17.56 17.22H6.44C2.89 17.22 2 16.33 2 12.78V6.44C2 2.89 2.89 2 6.44 2H8" stroke="#292D32" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M12 17.22V22" stroke="#292D32" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M2 13H22" stroke="#292D32" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M7.5 22H16.5" stroke="#292D32" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M17.8598 9.37001H13.0998C11.7198 9.37001 11.2598 8.45001 11.2598 7.53001V4.01001C11.2598 2.91001 12.1598 2.01001 13.2598 2.01001H17.8598C18.8798 2.01001 19.6998 2.83001 19.6998 3.85001V7.53001C19.6998 8.55001 18.8798 9.37001 17.8598 9.37001Z" stroke="#292D32" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M20.9092 7.91998L19.6992 7.06998V4.30998L20.9092 3.45998C21.5092 3.04998 21.9992 3.29998 21.9992 4.02998V7.35998C21.9992 8.08998 21.5092 8.33998 20.9092 7.91998Z" stroke="#292D32" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  );
};

const WhatYouGainIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M19 9C19 10.45 18.57 11.78 17.83 12.89C16.75 14.49 15.04 15.62 13.05 15.91C12.71 15.97 12.36 16 12 16C11.64 16 11.29 15.97 10.95 15.91C8.96 15.62 7.25 14.49 6.17 12.89C5.43 11.78 5 10.45 5 9C5 5.13 8.13 2 12 2C15.87 2 19 5.13 19 9Z" stroke="#292D32" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M21.2491 18.47L19.5991 18.86C19.2291 18.95 18.9391 19.23 18.8591 19.6L18.5091 21.07C18.3191 21.87 17.2991 22.11 16.7691 21.48L11.9991 16L7.2291 21.49C6.6991 22.12 5.6791 21.88 5.4891 21.08L5.1391 19.61C5.0491 19.24 4.7591 18.95 4.3991 18.87L2.7491 18.48C1.9891 18.3 1.7191 17.35 2.2691 16.8L6.1691 12.9C7.2491 14.5 8.9591 15.63 10.9491 15.92C11.2891 15.98 11.6391 16.01 11.9991 16.01C12.3591 16.01 12.7091 15.98 13.0491 15.92C15.0391 15.63 16.7491 14.5 17.8291 12.9L21.7291 16.8C22.2791 17.34 22.0091 18.29 21.2491 18.47Z" stroke="#292D32" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M12.58 5.98L13.17 7.15999C13.25 7.31999 13.46 7.48 13.65 7.51L14.72 7.68999C15.4 7.79999 15.56 8.3 15.07 8.79L14.24 9.61998C14.1 9.75998 14.02 10.03 14.07 10.23L14.31 11.26C14.5 12.07 14.07 12.39 13.35 11.96L12.35 11.37C12.17 11.26 11.87 11.26 11.69 11.37L10.69 11.96C9.96997 12.38 9.53997 12.07 9.72997 11.26L9.96997 10.23C10.01 10.04 9.93997 9.75998 9.79997 9.61998L8.96997 8.79C8.47997 8.3 8.63997 7.80999 9.31997 7.68999L10.39 7.51C10.57 7.48 10.78 7.31999 10.86 7.15999L11.45 5.98C11.74 5.34 12.26 5.34 12.58 5.98Z" stroke="#292D32" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  );
}

/** Figma 3583:10835 — mobile carousel control: 44px round nav, gap 20, 6px track */
const MobileProgramsCarouselSlider: FC<{
  currentIndex: number;
  totalCount: number;
  onPrev: () => void;
  onNext: () => void;
  className?: string;
}> = ({ currentIndex, totalCount, onPrev, onNext, className }) => {
  const canPrev = totalCount > 0 && currentIndex > 0;
  const canNext = totalCount > 0 && currentIndex < totalCount - 1;
  const n = Math.max(1, totalCount);
  const segmentWidthPct = 100 / n;
  const leftPct = (currentIndex / n) * 100;

  return (
    <div
      className={cn(
        "mx-auto flex w-full max-w-[345px] items-center gap-[20px]",
        className
      )}
    >
      <button
        type="button"
        onClick={onPrev}
        disabled={!canPrev}
        className={cn(
          "flex size-[44px] shrink-0 items-center justify-center rounded-[80px] border border-solid border-[#342b7c] bg-transparent p-[10px] disabled:opacity-50"
        )}
        aria-label="Previous"
      >
        <LeftArrow className="size-6 [&_path]:stroke-[#342b7c]" />
      </button>
      <div className="relative h-[6px] min-h-[6px] min-w-0 flex-1 rounded-[10px] bg-[rgba(52,43,124,0.1)]">
        <div
          className="absolute top-0 h-full rounded-[10px] bg-[#342b7c] transition-[left,width] duration-300 ease-out"
          style={{
            width: `${segmentWidthPct}%`,
            left: `${leftPct}%`,
          }}
        />
      </div>
      <button
        type="button"
        onClick={onNext}
        disabled={!canNext}
        className={cn(
          "flex size-[44px] shrink-0 items-center justify-center rounded-[80px] border border-solid border-[#342b7c] bg-transparent p-[10px] disabled:opacity-50"
        )}
        aria-label="Next"
      >
        <RightArrow className="size-6 [&_path]:stroke-[#342b7c]" />
      </button>
    </div>
  );
};

/** Figma vuesax/linear/send as diagonal up-right arrow; scales with currentColor */
const ExploreCourseArrow: FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    className={cn("shrink-0", className)}
    aria-hidden
  >
    <path
      d="M7 17L17 7M17 7H10M17 7V14"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** vuesax/linear/arrow-down style — for brochure CTA (Explore uses up-right send arrow). */
const BrochureDownloadArrow: FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    className={cn("shrink-0", className)}
    aria-hidden
  >
    <path
      d="M12.0017 3.5V20.5M12.0017 20.5L5.5 14M12.0017 20.5L18.5 14"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Section CTA trailing icon — up-right arrow (no CSS flip; transforms pointed the wrong way) */
const CtaTrailingArrow: FC<{ className?: string }> = ({ className }) => (
  <ExploreCourseArrow className={cn("size-[18px] shrink-0", className)} />
);

const PROGRAM_CARDS_2_CTA_WMC_BUTTON_CLASSNAME = cn(
  "w-fit max-w-full shrink-0 rounded-lg border-0 outline-none ring-0 transition-opacity hover:opacity-95",
  "flex items-center justify-center gap-[6px] font-semibold leading-[1.49]",
  "px-[16px] py-[8px] text-[14px]",
  "sm:gap-[6px] sm:px-[14px] sm:py-[6px] sm:text-[14px]",
  "md:gap-[6px] md:px-4 md:py-1.5 md:text-[16px]",
  "lg:gap-2.5 lg:px-6 lg:py-2 lg:text-[16px]",
  "xl:gap-2.5 xl:px-6 xl:py-2 xl:text-[16px]",
  "focus-visible:ring-2 focus-visible:ring-[#342b7c] focus-visible:ring-offset-2"
);

const PROGRAM_CARDS_2_CTA_WMC_ICON_WRAPPER_CLASSNAME = cn(
  "relative shrink-0 size-[18px]",
  "sm:size-[26px]",
  "md:size-8",
  "lg:size-9",
  "xl:size-9"
);

export type ProgramCards2CtaButtonVariant = "variant_1" | "variant_2";

const boolish = z
  .union([z.boolean(), z.number(), z.null()])
  .optional()
  .transform((v) => (v === undefined || v === null ? false : v === true || v === 1));

/** Section `show_features` only: value may be null; on only when strictly `true` or `1`. */
const showFeaturesBoolish = z
  .union([z.boolean(), z.number(), z.null()])
  .optional()
  .transform((v) => v === true || v === 1);

/** Default on when omitted; off only for `false` or `0`. */
const useCategoriesBoolish = z
  .union([z.boolean(), z.number(), z.null()])
  .optional()
  .transform((v) => !(v === false || v === 0));

/** Section header, card copy, What You'll Gain, and modal title — truncate when exceeded (Figma text layers / dev mode). */
const PROGRAM_CARDS_2_COPY_LIMITS = {
  preTitle: 20,
  sectionTitle: 20,
  cardTitle: 20,
  cardSubText: 100,
  whatYouGainTitle: 50,
  whatYouGainSubText: 100,
  modalTitle: 40,
  brochureButtonText: 40,
} as const;

function clipProgramCards2Text(value: unknown, maxLen: number): string {
  const s = String(value ?? "");
  return s.length <= maxLen ? s : s.slice(0, maxLen);
}

/** Optional API fields often arrive as `null`; Zod `.optional()` alone rejects that. */
function pc2StrDef(defaultValue: string) {
  return z
    .union([z.string(), z.null()])
    .optional()
    .transform((v) => (v == null || v === undefined ? defaultValue : String(v)));
}

function pc2StrClip(maxLen: number, defaultValue: string) {
  return z
    .union([z.string(), z.null()])
    .optional()
    .transform((v) =>
      clipProgramCards2Text(v == null || v === undefined ? defaultValue : String(v), maxLen)
    );
}

function pc2NumDef(defaultValue: number) {
  return z
    .union([z.number(), z.null()])
    .optional()
    .transform((v) => {
      if (v === undefined || v === null) return defaultValue;
      const n = typeof v === "number" ? v : Number(v);
      return Number.isFinite(n) ? n : defaultValue;
    });
}

function pc2OptBool() {
  return z.union([z.boolean(), z.null()]).optional();
}

function pc2OptStr() {
  return z.union([z.string(), z.null()]).optional();
}

function pc2OptNum() {
  return z.union([z.number(), z.null()]).optional();
}

/** Show a trait row only when both label and body are non-empty (Figma). */
function programCards2TraitPairOk(heading: unknown, body: unknown): boolean {
  return Boolean(String(heading ?? "").trim() && String(body ?? "").trim());
}

/** Card has no modal_title, modal_description, or modal_cta_text — Explore opens lead drop, not the detail modal. */
export function programCards2CardOpenLeadDirectNoModalContent(card: {
  modal_title?: unknown;
  modal_description?: unknown;
  modal_cta_text?: unknown;
}): boolean {
  return (
    !String(card.modal_title ?? "").trim() &&
    !String(card.modal_description ?? "").trim() &&
    !String(card.modal_cta_text ?? "").trim()
  );
}

/**
 * Explore CTA resolved as a link, or `null` when the card keeps the modal / lead-drop
 * behaviour.
 *
 * `explore_button_behavior === "redirect"` is the admin's "Go to URL" choice and it
 * OUTRANKS every other explore outcome — in particular the "card has no modal content
 * ⇒ open the lead form" fallback, which otherwise swallows it: a link-mode card
 * usually has no modal copy, so it matched that fallback and opened the lead form
 * instead of navigating.
 *
 * A redirect card with a blank URL must still not open the lead form — the admin
 * chose a link and simply has not filled it in — so it resolves to "#", which stays
 * on the current page.
 */
export function programCards2CardExploreHref(card: {
  explore_button_behavior?: unknown;
  explore_button_redirect_url?: unknown;
}): string | null {
  if (String(card.explore_button_behavior ?? "").trim() !== "redirect") return null;
  return String(card.explore_button_redirect_url ?? "").trim() || "#";
}

/** Off-site link (or mail/tel) → new tab; in-app path and the "#" placeholder stay put. */
export function programCards2ExploreHrefIsExternal(href: string): boolean {
  return /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i.test(href);
}

/** Program cards API category row (+ nested cards) */
const CategorySchema = z.object({
  id: z.union([z.number(), z.string()]).nullable().optional(),
  program_cards_id: z.union([z.number(), z.string()]).nullable().optional(),
  name: pc2StrDef(""),
  is_uncategorized_bucket: pc2OptBool(),
  order_index: pc2NumDef(0),
  is_default: z.union([z.boolean(), z.number(), z.null()]).optional(),
  text_color: pc2StrDef("#000000"),
  background_color: pc2StrDef("#ffffff"),
  active_text_color: pc2StrDef("#FFFFFF"),
  active_background_color: pc2StrDef("#02A7B6"),
  border_color: pc2StrDef("rgba(0,0,0,0.4)"),
  status: boolish,
  is_deleted: pc2OptNum(),
  created_by: z.union([z.number(), z.null()]).optional(),
  updated_by: z.union([z.number(), z.null()]).optional(),
  createdAt: pc2OptStr(),
  updatedAt: pc2OptStr(),
  cards: z
    .union([z.array(z.record(z.string(), z.unknown())), z.null()])
    .optional()
    .transform((v) => v ?? []),
});

/** Single source for section main CTA defaults (Zod defaults + variant_1 solid fill). */
export const PROGRAM_CARDS_2_MAIN_CTA_DEFAULTS = {
  cta_text_color: "#FFFFFF",
  cta_background_start_color: "#02A7B6",
  cta_background_end_color: "#342B7C",
  cta_background_angle: "180deg",
} as const;

/** Per-card modal CTA colors: empty means “inherit section CTA gradient” (see `resolvedFooterCtaStyle`). */
const optionalModalCtaString = z
  .union([z.string(), z.null()])
  .optional()
  .transform((v) => (v == null ? "" : String(v).trim()));

/** Program cards API card row (variant 2) — field order matches API */
const CardSchema = z.object({
  id: z.union([z.number(), z.string()]).nullable().optional(),
  program_cards_id: z.union([z.number(), z.string(), z.null()]).optional(),
  category_id: z.union([z.number(), z.string(), z.null()]).optional(),
  title: pc2StrClip(PROGRAM_CARDS_2_COPY_LIMITS.cardTitle, ""),
  title_color: pc2StrDef("#342B7C"),
  subtitle: pc2StrClip(PROGRAM_CARDS_2_COPY_LIMITS.cardSubText, ""),
  subtitle_color: pc2StrDef("#00000099"),
  image: pc2StrDef(""),
  image_alt: pc2StrDef(""),
  duration_heading: pc2StrDef("Duration"),
  duration_text: pc2StrDef(""),
  duration_icon: pc2StrDef(""),
  duration_icon_alt: pc2StrDef(""),
  duration_heading_color: pc2StrDef("#000000"),
  duration_text_color: pc2StrDef("#00000099"),
  format_heading: pc2StrDef("Format"),
  format_text: pc2StrDef(""),
  format_icon: pc2StrDef(""),
  format_icon_alt: pc2StrDef(""),
  format_heading_color: pc2StrDef("#000000"),
  format_text_color: pc2StrDef("#00000099"),
  what_you_gain_heading: pc2StrClip(
    PROGRAM_CARDS_2_COPY_LIMITS.whatYouGainTitle,
    "What You'll Gain"
  ),
  what_you_gain_text: pc2StrClip(PROGRAM_CARDS_2_COPY_LIMITS.whatYouGainSubText, ""),
  what_you_gain_icon: pc2StrDef(""),
  what_you_gain_icon_alt: pc2StrDef(""),
  what_you_gain_heading_color: pc2StrDef("#000000"),
  what_you_gain_text_color: pc2StrDef("#00000099"),
  button_text: pc2StrDef("Explore Course"),
  button_text_color: pc2StrDef("#02A7B6"),
  button_background_color: z
    .union([z.string(), z.null()])
    .optional()
    .transform((v) => {
      if (v == null) return "transparent";
      const s = String(v).trim();
      return s === "" ? "transparent" : s;
    }),
  button_icon: pc2StrDef(""),
  button_icon_alt: pc2StrDef(""),
  /** Admin "Explore button behaviour": `open_modal` (default) or `redirect` ("Go to URL"). */
  explore_button_behavior: pc2StrDef("open_modal"),
  explore_button_redirect_url: pc2StrDef(""),
  modal_title: pc2StrClip(PROGRAM_CARDS_2_COPY_LIMITS.modalTitle, ""),
  modal_title_color: pc2StrDef("#FFFFFF"),
  modal_title_background_start_color: pc2StrDef("#342B7C"),
  modal_title_background_end_color: pc2StrDef("#02A7B6"),
  modal_title_background_angle: pc2StrDef("90deg"),
  modal_description: pc2StrDef(""),
  modal_cta_text: z
    .union([z.string(), z.null()])
    .optional()
    .transform((v) => (v == null ? "" : String(v).trim())),
  modal_cta_background_start_color: optionalModalCtaString,
  modal_cta_background_end_color: optionalModalCtaString,
  modal_cta_background_angle: optionalModalCtaString,
  modal_cta_text_color: optionalModalCtaString,
  brochure_button_text: z
    .union([z.string(), z.null()])
    .optional()
    .transform((v) =>
      clipProgramCards2Text(v == null ? "" : String(v), PROGRAM_CARDS_2_COPY_LIMITS.brochureButtonText)
    ),
  brochure_button_text_color: z
    .union([z.string(), z.null()])
    .optional()
    .transform((v) => {
      if (v == null) return "#FFFFFF";
      const s = String(v).trim();
      return s === "" ? "#FFFFFF" : s;
    }),
  brochure_button_background_color: z
    .union([z.string(), z.null()])
    .optional()
    .transform((v) => {
      if (v == null) return "#342B7C";
      const s = String(v).trim();
      return s === "" ? "#342B7C" : s;
    }),
  brochure_button_icon: pc2StrDef(""),
  brochure_button_icon_alt: pc2StrDef(""),
  brochure_link: z
    .union([z.string(), z.null()])
    .optional()
    .transform((v) => (v == null ? "" : String(v).trim())),
  priority: pc2NumDef(0),
  status: boolish,
  is_deleted: pc2OptNum(),
  created_by: z.union([z.number(), z.null()]).optional(),
  updated_by: z.union([z.number(), z.null()]).optional(),
  createdAt: pc2OptStr(),
  updatedAt: pc2OptStr(),
});

/** Program cards API section payload (GET / variant "2") */
export const ProgramCards2DataSchema = z.object({
  id: z.union([z.number(), z.string()]).nullable().optional(),
  page_id: z.union([z.number(), z.null()]).optional(),
  component_id: z.union([z.number(), z.null()]).optional(),
  title: pc2StrClip(PROGRAM_CARDS_2_COPY_LIMITS.sectionTitle, "Every Stage"),
  title_text_color: pc2StrDef("#000000"),
  card_background_color: pc2StrDef("#ffffff"),
  load_more_text: pc2StrDef(""),
  load_more_desktop_icon: pc2StrDef(""),
  load_more_desktop_icon_alt: pc2StrDef(""),
  load_more_mobile_icon: pc2StrDef(""),
  load_more_mobile_icon_alt: pc2StrDef(""),
  load_more_desktop_text_color: pc2StrDef("#000000"),
  load_more_mobile_text_color: pc2StrDef("#000000"),
  load_more_mobile_background_color: pc2StrDef("#ffffff"),
  desktop_background_color: pc2StrDef("#FFFFFF"),
  mobile_background_color: pc2StrDef("#FFFFFF"),
  backvariant: z
    .union([z.enum(["variant_1", "variant_2", "variant_3"]), z.null()])
    .optional()
    .transform((v) => (v == null || v === undefined ? "variant_3" : v)),
  font_family_variant: z
    .union([z.enum(["variant_1", "variant_2"]), z.null()])
    .optional()
    .transform((v) => (v == null || v === undefined ? "variant_2" : v)),
  gradient_color_1: pc2StrDef("#5345C3"),
  gradient_color_2: pc2StrDef("#000000"),
  gradient_color_3: pc2StrDef("#000000"),
  gradient_color_4: pc2StrDef("#00D4EB"),
  pre_title: pc2StrClip(PROGRAM_CARDS_2_COPY_LIMITS.preTitle, "Programs Built for"),
  pre_title_color: pc2StrDef("#222222CC"),
  title_start_color: pc2StrDef("#342B7C"),
  title_end_color: pc2StrDef("#02A7B6"),
  title_gradient_angle: pc2StrDef("180deg"),
  title_1: pc2StrDef(""),
  title_1_color: pc2StrDef("#000000"),
  title_2: pc2StrDef(""),
  title_2_gradient_start_color: pc2StrDef("#FFFFFF"),
  title_2_gradient_end_color: pc2StrDef("#02A7B6"),
  title_2_color: pc2StrDef("#000000"),
  cta_text: pc2StrDef("View All Course"),
  cta_text_color: pc2StrDef(PROGRAM_CARDS_2_MAIN_CTA_DEFAULTS.cta_text_color),
  cta_background_start_color: pc2StrDef(PROGRAM_CARDS_2_MAIN_CTA_DEFAULTS.cta_background_start_color),
  cta_background_end_color: pc2StrDef(PROGRAM_CARDS_2_MAIN_CTA_DEFAULTS.cta_background_end_color),
  cta_background_angle: pc2StrDef(PROGRAM_CARDS_2_MAIN_CTA_DEFAULTS.cta_background_angle),
  cta_url: z
    .union([z.string(), z.null()])
    .optional()
    .transform((v) => v ?? ""),
  cta_icon: pc2StrDef(""),
  cta_icon_alt: pc2StrDef(""),
  cta_button_variant: z.preprocess(
    (val) => (val === null || val === undefined ? undefined : val),
    z.enum(["variant_1", "variant_2"]).catch("variant_2")
  ),
  explore_course_text: pc2StrDef("Explore Course"),
  explore_course_text_color: pc2StrDef("#02A7B6"),
  explore_course_border_color: pc2StrDef("#02A7B6"),
  explore_course_icon: pc2StrDef(""),
  explore_course_icon_alt: pc2StrDef(""),
  explore_all_courses_text: pc2StrDef("View All Course"),
  modal_cta_text: pc2StrDef("Apply Now"),
  show_features: showFeaturesBoolish,
  /** Scroll-reveal animations (RevealOnScroll); on only for `true` or `1`. */
  enable_animation: showFeaturesBoolish,
  use_categories: useCategoriesBoolish,
  metadata: z
    .union([z.array(z.unknown()), z.null()])
    .optional()
    .transform((v) => v ?? []),
  variant: pc2OptStr(),
  status: boolish,
  is_deleted: pc2OptNum(),
  created_by: z.union([z.number(), z.null()]).optional(),
  updated_by: z.union([z.number(), z.null()]).optional(),
  createdAt: pc2OptStr(),
  updatedAt: pc2OptStr(),
  categories: z
    .union([z.array(CategorySchema), z.null()])
    .optional()
    .transform((v) => v ?? []),
}).transform((data) => ({
  ...data,
  categories: (data.categories || []).map((cat) => ({
    ...cat,
    cards: (cat.cards || []).map((c) => ({ ...CardSchema.parse(c), priority: Number((c as any)?.priority) || 0 })).sort((a, b) => a.priority - b.priority),
  })).sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0)),
}));

/** Parsed card row; aligns with {@link IProgramCardsCard} (category_id optional when omitted by CMS) */
export type ProgramCards2CardType = z.infer<typeof CardSchema>;
type CardType = ProgramCards2CardType;
type ProgramCards2Parsed = z.infer<typeof ProgramCards2DataSchema>;

function isDefaultCategory(cat: Pick<IProgramCardsCategory, "is_default"> | undefined): boolean {
  const v: any = cat?.is_default;
  return v === true || v === 1 || v === "1";
}

function isUncategorizedProgramCardsCategory(cat: { id?: unknown; is_uncategorized_bucket?: boolean } | undefined): boolean {
  if (!cat) return false;
  return Boolean(cat.is_uncategorized_bucket) || Number(cat.id) === -1;
}

/** Card W × min-h (px) — Figma 915:12798, 1303:11798, 1299:11665, 915:12590, 1303:11936. Grid gap 20px (sm+). */
const PROGRAM_CARDS_2_CARD_FRAME = cn(
  "box-border w-full max-w-[345px] min-h-[510px]",
  "sm:min-h-[367px] sm:shrink-0 sm:w-full sm:max-w-full",
  "md:min-h-[444px]",
  "lg:min-h-[466px]",
  "xl:min-h-[558px]"
);

/** Centers a fixed-Figma-width card inside each grid column. */
const PROGRAM_CARDS_2_DESKTOP_CARD_GRID_CELL = "flex w-full min-w-0 justify-center";

/** Modal footer CTA — align with section primary CTA (Figma 1039:8149, paddings 3583:10841 / sm 3574) */
type ModalFooterCtaStyle = {
  angle: string;
  start: string;
  end: string;
  color: string;
};

/** Course detail modal — header Figma 1033:7721; body 1033:7847 (CMS HTML); typography uses Program Cards 2 font maps. */
const ProgramCardDetailModal: FC<{
  card: CardType | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Section default when card `modal_cta_text` is empty. */
  footerCtaText: string;
  footerCtaStyle: ModalFooterCtaStyle;
  fonts: ProgramCards2FontFamilyMap;
  ctaIconUrl?: string;
  ctaIconAlt?: string;
  ctaButtonVariant?: ProgramCards2CtaButtonVariant;
  /** Closes this dialog then opens lead drop (same flow as Banner3 apply CTA). */
  onFooterCtaClick: () => void;
}> = ({
  card,
  open,
  onOpenChange,
  footerCtaText,
  footerCtaStyle,
  fonts,
  ctaIconUrl,
  ctaIconAlt,
  ctaButtonVariant = "variant_2",
  onFooterCtaClick,
}) => {
  if (!card) return null;

  const ang = card.modal_cta_background_angle?.trim();
  const modalCtaStart = card.modal_cta_background_start_color?.trim();
  const modalCtaEnd = card.modal_cta_background_end_color?.trim();
  const modalCtaColor = card.modal_cta_text_color?.trim();
  const resolvedFooterCtaStyle: ModalFooterCtaStyle = {
    angle: ang || footerCtaStyle.angle,
    start: modalCtaStart || footerCtaStyle.start,
    end: modalCtaEnd || footerCtaStyle.end,
    color: modalCtaColor || footerCtaStyle.color,
  };

  const title = clipProgramCards2Text(
    card.modal_title?.trim() || card.title || "Course details",
    PROGRAM_CARDS_2_COPY_LIMITS.modalTitle
  );
  const angle = card.modal_title_background_angle?.trim() || "90deg";
  const start = card.modal_title_background_start_color?.trim() || "#342B7C";
  const end = card.modal_title_background_end_color?.trim() || "#02A7B6";
  const titleColor = card.modal_title_color || "#FFFFFF";
  const resolvedFooterCtaLabel =
    card.modal_cta_text?.trim() || footerCtaText?.trim() || "Apply Now";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="z-[100] bg-black/50" />
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 pointer-events-none sm:p-6">
          <DialogPrimitive.Content
            className={cn(
              "pointer-events-auto flex h-[min(78dvh,540px)] max-h-[92dvh] w-full max-w-[min(100vw-2rem,560px)] flex-col overflow-hidden rounded-2xl border border-black/10 bg-white shadow-xl outline-none",
              "sm:h-[min(72dvh,640px)] sm:max-h-[85dvh]",
              "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
            )}
            style={{ fontFamily: fonts.body }}
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <DialogPrimitive.Title className="sr-only">{title}</DialogPrimitive.Title>
            <DialogPrimitive.Description className="sr-only">
              Program description and details
            </DialogPrimitive.Description>

            <div
              className={cn(
                "relative flex min-h-[64px] shrink-0 items-center justify-between gap-4",
                "px-6 py-4 md:min-h-[80px] md:px-10 md:py-5"
              )}
              style={{
                backgroundImage: `linear-gradient(${angle}, ${start}, ${end})`,
              }}
            >
              <h2
                className={cn(
                  "min-w-0 flex-1 text-left font-bold leading-[1.2] tracking-[1.2px]",
                  "text-[22px] sm:text-[32px] md:text-[40px] lg:text-[40px]"
                )}
                style={{ color: titleColor, fontFamily: fonts.primaryHeading }}
              >
                {title}
              </h2>
              <DialogPrimitive.Close
                type="button"
                className={cn(
                  "flex size-10 shrink-0 items-center justify-center rounded-full text-white transition-colors",
                  "hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/90 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
                )}
                aria-label="Close"
              >
                <X className="size-5" strokeWidth={2} aria-hidden />
              </DialogPrimitive.Close>
            </div>

            <div
              className={cn(
                "min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-6 py-4",
                "md:px-10 md:pt-5 md:pb-10",
                "text-[16px] font-medium leading-[1.49] text-black [word-break:break-word]",
                "[&_a]:[word-break:break-word] [&_img]:h-auto [&_img]:max-w-full [&_p]:mb-4 [&_p:last-child]:mb-0 [&_table]:max-w-full [&_ul]:list-disc [&_ul]:pl-5",
                "[&_h1]:text-[24px] [&_h1]:font-bold [&_h1]:leading-[1.49] [&_h1]:text-[#342b7c] [&_h1]:mb-4",
                "[&_h2]:text-[24px] [&_h2]:font-bold [&_h2]:leading-[1.49] [&_h2]:text-[#342b7c] [&_h2]:mb-4",
                "[&_h3]:text-[24px] [&_h3]:font-bold [&_h3]:leading-[1.49] [&_h3]:text-[#342b7c] [&_h3]:mb-4"
              )}
              style={{ fontFamily: fonts.body }}
              dangerouslySetInnerHTML={{ __html: card.modal_description || "" }}
            />

            {resolvedFooterCtaLabel ? (
              <div
                className={cn(
                  "flex w-full shrink-0 justify-center border-t border-black/10",
                  "px-6 py-5 md:px-10"
                )}
              >
                <button
                  type="button"
                  onClick={onFooterCtaClick}
                  className={cn(
                    "inline-flex max-w-full shrink-0 cursor-pointer items-center justify-center rounded-none border-0 font-semibold leading-[1.49] whitespace-nowrap outline-none ring-0 transition-opacity hover:opacity-95",
                    "box-border gap-[10px] px-[24px] py-[12px] text-[14px]",
                    "focus-visible:ring-2 focus-visible:ring-[#342b7c] focus-visible:ring-offset-2"
                  )}
                  style={{
                    backgroundImage: `linear-gradient(${resolvedFooterCtaStyle.angle}, ${resolvedFooterCtaStyle.start}, ${resolvedFooterCtaStyle.end})`,
                    color: resolvedFooterCtaStyle.color,
                    fontFamily: fonts.cta,
                  }}
                >
                  {resolvedFooterCtaLabel}
                  {ctaIconUrl?.trim() ? (
                    <Image
                      src={ctaIconUrl.trim()}
                      alt={ctaIconAlt?.trim() || ""}
                      width={18}
                      height={18}
                      className="size-[18px] shrink-0 object-contain"
                    />
                  ) : (
                    <CtaTrailingArrow className="text-current" />
                  )}
                </button>
              </div>
            ) : null}
          </DialogPrimitive.Content>
        </div>
      </DialogPortal>
    </Dialog>
  );
};

// Program card — W × min-h (px): mobile 345×510, sm 307×367, md 343×444, lg 410×466, xl 477×558; grid gap 20
const ProgramCard: FC<{
  card: CardType;
  sectionShowFeatures: boolean;
  /** When false, `RevealOnScroll` wrappers render children without motion. */
  enableAnimation: boolean;
  className?: string;
  onExploreClick?: () => void;
  /** Per-card brochure; only used when `card.brochure_link` is set. */
  onBrochureClick?: () => void;
  fonts: ProgramCards2FontFamilyMap;
}> = ({
  card,
  sectionShowFeatures,
  enableAnimation,
  className,
  onExploreClick,
  onBrochureClick,
  fonts,
}) => {
  /** Non-null ⇒ admin picked "Go to URL"; the CTA renders as a link, never a lead-form trigger. */
  const exploreHref = programCards2CardExploreHref(card);
  const canExplore =
    exploreHref !== null ||
    Boolean(card.modal_description?.trim()) ||
    programCards2CardOpenLeadDirectNoModalContent(card);
  const showBrochure = Boolean(card.brochure_link?.trim() && onBrochureClick);
  const brochureBg = (card.brochure_button_background_color || "#342B7C").trim();
  const brochureFg = (card.brochure_button_text_color || "#FFFFFF").trim();
  const exploreBg = (card.button_background_color || "transparent").trim() || "transparent";
  const exploreFg = (card.button_text_color || "#02A7B6").trim();
  const showDuration = sectionShowFeatures && programCards2TraitPairOk(card.duration_heading, card.duration_text);
  const showFormat = sectionShowFeatures && programCards2TraitPairOk(card.format_heading, card.format_text);
  const showWyg =
    sectionShowFeatures && programCards2TraitPairOk(card.what_you_gain_heading, card.what_you_gain_text);

  /** Icon ↔ copy — Figma 1303:11806/11811 (sm gap 8), 1299:11673/11678, 915:12598/12603, 1303:11944/11949 (md+ gap 16). */
  const traitRowGap = "gap-[10.78px] sm:gap-2 sm:min-w-0 md:gap-4";

  /* Explore CTA renders as <a> ("Go to URL") or <button>; identical look either way. */
  const exploreCtaClassName = cn(
    "inline-flex box-border items-center justify-center border-solid font-semibold leading-[1.49] whitespace-nowrap",
    showBrochure
      ? "min-w-0 flex-1 basis-0 sm:basis-auto sm:min-w-0 sm:w-full"
      : "w-full sm:w-auto sm:justify-self-start",
    /* 7654:7884 — row h 38px */
    "min-h-[38px] gap-[2.74px] rounded-[26.95px] border-[2.021px] px-[14px] py-[10px] text-[12.127px]",
    /* 7654:7894 (h 43) + 8182:3907/3967 (h 46) */
    "sm:min-h-[43px] sm:gap-2.5 sm:rounded-[40px] sm:border-2 sm:px-6 sm:py-[11px] sm:text-[14px]",
    "md:min-h-[46px] md:text-[16px] lg:min-h-[46px]",
    /* 7654:8017 — h 52 */
    "xl:min-h-[52px] xl:gap-2.5 xl:border-[3px] xl:px-6 xl:py-[14px] xl:text-[16px]"
  );
  const exploreCtaStyle = {
    fontFamily: fonts.cta,
    color: exploreFg,
    backgroundColor: exploreBg,
    borderColor: exploreBg,
  } as CSSProperties;
  const exploreCtaContent = (
    <>
      {card.button_text}
      {card.button_icon?.trim() ? (
        <Image
          src={card.button_icon.trim()}
          alt={card.button_icon_alt?.trim() || ""}
          width={18}
          height={18}
          className="size-[12.127px] shrink-0 object-contain sm:size-[18px]"
        />
      ) : (
        <ExploreCourseArrow className="size-[12.127px] shrink-0 sm:size-[18px]" />
      )}
    </>
  );

  const durationBlock = showDuration && (
    <div className={cn("flex w-full items-center", traitRowGap)}>
      {card.duration_icon ? (
        /* No `w-full h-full` here: this wraps a ~16-24px trait ICON inside a
           `flex w-full items-center` row, so a full-width wrapper stretches the icon's box
           across the whole row and pushes the label off the card (measured: 20px -> 297px
           at 360px wide). That is the card-IMAGE pattern — still correct further down,
           where an `Image` with `fill` needs a sized box — copy-pasted onto an icon. The
           no-icon branch below passes no className either. */
        <RevealOnScroll enableAnimation={enableAnimation} scaleStart={0.5}>
          <Image
            src={card.duration_icon}
            alt={card.duration_icon_alt || ""}
            width={24}
            height={24}
            className="size-[16.17px] sm:size-[20px] md:size-6"
          />
        </RevealOnScroll>
      ) : (
        <RevealOnScroll enableAnimation={enableAnimation} scaleStart={0.5}>
        <div
          className={cn(
            "box-border flex shrink-0 items-center justify-center border border-black/50",
            "size-[29.64478683px] rounded-[15px] p-[6.737px] max-sm:mt-0.5",
            "sm:size-9 sm:rounded-[12px] sm:border-[0.818px] sm:p-2",
            "md:size-11 md:rounded-[15px] md:border md:p-[10px] lg:mt-0.5 xl:mt-0.5"
          )}
        >
          <DurationIcon />
        </div>
        </RevealOnScroll>
      )}
      <div
        className="flex w-full min-w-0 shrink-0 flex-col leading-[1.49] sm:w-[71px]"
      >
        <p
          className="font-semibold text-[12px] sm:text-[12px] md:text-[14px] lg:text-[16px]"
          style={{ color: card.duration_heading_color, fontFamily: fonts.body }}
        >
          {card.duration_heading}
        </p>
        <p
          className="font-medium text-black/60 text-[10px] sm:text-[12px] md:text-[14px] lg:text-[16px]"
          style={{ color: card.duration_text_color, fontFamily: fonts.body }}
        >
          {card.duration_text}
        </p>
      </div>
    </div>
  );

  const formatBlock = showFormat && (
    <div className={cn("flex w-full items-center", traitRowGap)}>
      {card.format_icon ? (
        /* No `w-full h-full` here: this wraps a ~16-24px trait ICON inside a
           `flex w-full items-center` row, so a full-width wrapper stretches the icon's box
           across the whole row and pushes the label off the card (measured: 20px -> 297px
           at 360px wide). That is the card-IMAGE pattern — still correct further down,
           where an `Image` with `fill` needs a sized box — copy-pasted onto an icon. The
           no-icon branch below passes no className either. */
        <RevealOnScroll enableAnimation={enableAnimation} scaleStart={0.5}>
          <Image
            src={card.format_icon}
            alt={card.format_icon_alt || ""}
            width={24}
            height={24}
            className="size-[16.17px] sm:size-[20px] md:size-6"
          />
        </RevealOnScroll>
      ) : (
        <RevealOnScroll enableAnimation={enableAnimation} scaleStart={0.5}>
        <div
          className={cn(
            "box-border flex shrink-0 items-center justify-center border border-black/50",
            "size-[29.64478683px] rounded-[10.106px] p-[6.737px] max-sm:mt-0.5",
            "sm:size-9 sm:rounded-[12px] sm:border-[0.818px] sm:p-2",
            "md:size-11 md:rounded-[15px] md:border md:p-[10px] lg:mt-0.5 xl:mt-0.5"
          )}
        >
          <FormatIcon />
        </div>
        </RevealOnScroll>
      )}
      <div
        className={cn(
          "flex min-w-0 shrink-0 flex-col leading-[1.49]",
          "w-full sm:w-[91.33px] md:w-[93.67px] lg:w-[127px] xl:w-[148.33px]"
        )}
      >
        <p
          className="font-semibold text-[12px] sm:text-[12px] md:text-[14px] lg:text-[16px]"
          style={{ color: card.format_heading_color, fontFamily: fonts.body }}
        >
          {card.format_heading}
        </p>
        <p
          className="line-clamp-2 font-medium text-black/60 text-[10px] sm:text-[12px] md:text-[14px] lg:text-[16px]"
          style={{ color: card.format_text_color, fontFamily: fonts.body }}
        >
          {card.format_text}
        </p>
      </div>
    </div>
  );

  const wygBlock = showWyg && (
    <div className={cn("flex w-full items-center sm:col-span-2", traitRowGap)}>
      {card.what_you_gain_icon ? (
        /* No `w-full h-full` here: this wraps a ~16-24px trait ICON inside a
           `flex w-full items-center` row, so a full-width wrapper stretches the icon's box
           across the whole row and pushes the label off the card (measured: 20px -> 297px
           at 360px wide). That is the card-IMAGE pattern — still correct further down,
           where an `Image` with `fill` needs a sized box — copy-pasted onto an icon. The
           no-icon branch below passes no className either. */
        <RevealOnScroll enableAnimation={enableAnimation} scaleStart={0.5}>
          <Image
            src={card.what_you_gain_icon}
            alt={card.what_you_gain_icon_alt || ""}
            width={24}
            height={24}
            className="size-[16.17px] sm:size-[20px] md:size-6"
          />
        </RevealOnScroll>
      ) : (
        <RevealOnScroll enableAnimation={enableAnimation} scaleStart={0.5}>
        <div
          className={cn(
            "box-border flex shrink-0 items-center justify-center border border-black/50",
            "size-[29.64478683px] rounded-[10.106px] p-[6.737px] max-sm:mt-0.5",
            "sm:size-9 sm:rounded-[12px] sm:border-[0.818px] sm:p-2",
            "md:size-11 md:rounded-[15px] md:border md:p-[10px] lg:mt-0.5 xl:mt-0.5"
          )}
        >
          <WhatYouGainIcon />
        </div>
        </RevealOnScroll>
      )}
      <div className="flex min-w-0 flex-1 flex-col leading-[1.49]">
        <p
          className="font-semibold text-[12px] sm:text-[12px] md:text-[14px] lg:text-[16px]"
          style={{ color: card.what_you_gain_heading_color, fontFamily: fonts.body }}
        >
          {card.what_you_gain_heading}
        </p>
        <p
          className="line-clamp-2 font-medium text-black/60 text-[10px] sm:text-[12px] md:text-[14px] lg:text-[16px]"
          style={{ color: card.what_you_gain_text_color, fontFamily: fonts.body }}
        >
          {card.what_you_gain_text}
        </p>
      </div>
    </div>
  );

  const traitArea =
    showDuration || showFormat || showWyg ? (
      <div className="flex w-full flex-col gap-4 sm:grid sm:grid-cols-2 sm:gap-x-0 sm:gap-y-6">
        {durationBlock}
        {formatBlock}
        {wygBlock}
      </div>
    ) : null;

  return (
    <div
      className={cn(
        PROGRAM_CARDS_2_CARD_FRAME,
        "flex flex-col overflow-hidden border border-[rgba(52,43,124,0.4)] bg-[#FFFFFF]",
        "border-[0.404px] rounded-[6.737px] p-2 gap-2",
        "sm:border-[0.6px] sm:rounded-[10px] sm:p-2 sm:gap-1.5",
        "xl:p-[10px] xl:gap-1.5",
        className
      )}
    >
      <div
        className={cn(
          "relative w-full shrink-0 overflow-hidden rounded-[5.39px] bg-[#FFFFFF] sm:rounded-[8px]",
          "h-[183px] sm:h-[140px] md:h-[160px] lg:h-[190px] xl:h-[240px]"
        )}
      >
        {card.image ? (
          <RevealOnScroll
            enableAnimation={enableAnimation}
            className="w-full h-full"
            scaleStart={0.5}
          >
            <Image
              src={card.image}
              alt={card.image_alt || card.title || ""}
              fill
              className="bg-[#FFFFFF] object-cover"
            />
          </RevealOnScroll>
        ) : (
          <div className="h-full w-full bg-[#FFFFFF]" />
        )}
      </div>
      <div
        className={cn(
          "flex min-h-0 flex-1 flex-col",
          "gap-6 p-2 sm:gap-6 sm:p-[10px]",
          "md:gap-6 md:p-[10px] lg:gap-6 lg:p-[10px]",
          "xl:gap-[30px] xl:p-5"
        )}
      >
        <div className="flex flex-col gap-[6.737px] sm:gap-2 md:gap-2.5">
          <p
            className="line-clamp-1 font-semibold leading-[1.49] text-[18px] sm:text-[20px] md:text-[22px] lg:text-[24px]"
            style={{ color: card.title_color, fontFamily: fonts.secondaryHeading }}
          >
            {card.title}
          </p>
          <p
            className="break-words font-medium leading-[1.49] text-[12px] sm:text-[12px] md:text-[14px] lg:text-[16px]"
            style={{ color: card.subtitle_color, fontFamily: fonts.body }}
          >
            {card.subtitle}
          </p>
        </div>

        {traitArea}

        <RevealOnScroll enableAnimation={enableAnimation} y={50}>
        <div
          className={cn(
            "mt-auto w-full",
            "flex flex-row flex-wrap items-start gap-[14px]",
            "sm:grid sm:grid-cols-2 sm:gap-x-[10px] sm:gap-y-[10px] xl:gap-x-[14px] xl:gap-y-[14px]"
          )}
        >
      

          {exploreHref !== null ? (
            /* "Go to URL" card: a real link, so it navigates (and supports middle-click /
               open-in-new-tab) instead of routing through the lead-drop handler. */
            <a
              href={exploreHref}
              {...(programCards2ExploreHrefIsExternal(exploreHref)
                ? { target: "_blank", rel: "noopener noreferrer" }
                : {})}
              onClick={() => onExploreClick?.()}
              className={cn(exploreCtaClassName, "cursor-pointer")}
              style={exploreCtaStyle}
            >
              {exploreCtaContent}
            </a>
          ) : (
            <button
              type="button"
              disabled={!canExplore || !onExploreClick}
              onClick={() => {
                if (canExplore && onExploreClick) onExploreClick();
              }}
              className={cn(
                exploreCtaClassName,
                "disabled:pointer-events-none disabled:opacity-50",
                canExplore && onExploreClick && "cursor-pointer"
              )}
              style={exploreCtaStyle}
              aria-label={
                canExplore
                  ? programCards2CardOpenLeadDirectNoModalContent(card)
                    ? `Open registration: ${card.button_text || "Explore"}`
                    : `Open details: ${card.button_text || "Explore"}`
                  : undefined
              }
            >
              {exploreCtaContent}
            </button>
          )}

          {showBrochure ? (
            <button
              type="button"
              data-cta-name="download_brochure"
              onClick={onBrochureClick}
              className={cn(
                "inline-flex min-h-[38px] min-w-0 flex-1 basis-0 flex-row items-center justify-center border-solid text-[12.127px] font-semibold leading-[1.49] whitespace-nowrap",
                "max-w-full sm:basis-auto sm:shrink-0 sm:max-w-full",
                "gap-[2.74px] rounded-[26.95px] border-[2.021px] px-[14px] py-[10px]",
                "sm:min-h-[43px] sm:min-w-0 sm:w-full sm:flex-row-reverse sm:gap-[5px] sm:rounded-[40px] sm:border-2 sm:px-6 sm:py-[11px] sm:text-[14px]",
                "md:min-h-[46px] md:text-[16px] lg:min-h-[46px]",
                "xl:min-h-[52px] xl:flex-row xl:gap-[10px] xl:border-[3px] xl:px-6 xl:py-[14px] xl:text-[16px]"
              )}
              style={
                {
                  color: brochureFg,
                  backgroundColor: brochureBg,
                  borderColor: brochureFg,
                  borderWidth: "2px",
                  fontFamily: fonts.cta,
                } as CSSProperties
              }
              aria-label={card.brochure_button_text?.trim() || "Download brochure"}
            >
              {card.brochure_button_text?.trim() || "Brochure"}
              {card.brochure_button_icon?.trim() ? (
                <Image
                  src={card.brochure_button_icon.trim()}
                  alt={card.brochure_button_icon_alt?.trim() || ""}
                  width={21}
                  height={21}
                  className="size-[15px] shrink-0 object-contain sm:size-[18px] xl:size-[21px]"
                />
              ) : (
                <BrochureDownloadArrow className="size-[15px] shrink-0 sm:size-[18px] xl:size-[21px] text-current" />
              )}
            </button>
          ) : null}
        </div>
        </RevealOnScroll>
      </div>
    </div>
  );
};

export function programCards2ModalCardSyncSignature(c: CardType): string {
  return [
    c.modal_title_background_angle,
    c.modal_title_background_start_color,
    c.modal_title_background_end_color,
    c.modal_title_color,
    c.modal_cta_background_angle,
    c.modal_cta_background_start_color,
    c.modal_cta_background_end_color,
    c.modal_cta_text_color,
    c.modal_cta_text,
    c.modal_title,
    c.modal_description,
  ].join("\0");
}

export const ProgramCards2: FC<any> = (props) => {
  const dispatch = useDispatch();
  const actualData = props?.data?.data || props?.data || props;
  /** Re-parse when nested fields change even if the parent keeps the same object reference. */
  const actualDataSnapshot = (() => {
    try {
      return JSON.stringify(actualData ?? null);
    } catch {
      return "";
    }
  })();
  const parsedResult = useMemo(
    () => ProgramCards2DataSchema.safeParse(actualData),
    [actualData, actualDataSnapshot],
  );

  const parsedData = parsedResult.success ? parsedResult.data : null;
  const categories = parsedData?.categories ?? [];
  const visibleCategories = useMemo(() => categories.slice(0, 4), [categories]);
  const showCategoryTabs =
    Boolean(parsedData?.use_categories) &&
    categories.some((c) => !isUncategorizedProgramCardsCategory(c as any));
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);
  useEffect(() => {
    const idx = visibleCategories.findIndex((c) => isDefaultCategory(c as any));
    setSelectedCategoryIndex(idx >= 0 ? idx : 0);
  }, [visibleCategories]);
  const sectionRef = useRef<HTMLElement>(null);
  const mobileScrollRef = useRef<HTMLDivElement>(null);
  const mobileCardRefsRef = useRef<(HTMLDivElement | null)[]>([]);
  const mobileScrollSyncLockRef = useRef(false);
  const mobileScrollRafRef = useRef<number | null>(null);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [detailModalCard, setDetailModalCard] = useState<CardType | null>(null);
  const [desktopShowAllGrid, setDesktopShowAllGrid] = useState(false);

  const isMobile = useIsMobile(1080);

  const openCardModal = useCallback(
    (card: CardType) => {
      // "Go to URL" card: the CTA is an <a>, which is navigating already. Must
      // return before the lead-drop fallback below (and its explore_direct_lead
      // event, which describes a lead outcome that no longer happens here).
      if (programCards2CardExploreHref(card) !== null) {
        return;
      }
      if (programCards2CardOpenLeadDirectNoModalContent(card)) {
        ctaClick({
          section: "program_cards_2",
          component: "explore_direct_lead",
          label: String(card.title ?? "Program"),
          step: "1",
          entity: "program_cards_2",
          parentEntity: "program_cards_2",
        });
        dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
        return;
      }
      if (card.modal_description?.trim()) {
        setDetailModalCard(card);
      }
    },
    [dispatch],
  );

  const fonts = useMemo(
    () =>
      parsedData?.font_family_variant === "variant_1"
        ? PROGRAM_CARDS_2_FONT_FAMILY_VARIANT_1_MAP
        : PROGRAM_CARDS_2_FONT_FAMILY_VARIANT_2_MAP,
    [parsedData?.font_family_variant],
  );

  useEffect(() => {
    if (!parsedData) return;
    setDetailModalCard((prev) => {
      if (!prev?.id) return prev;
      const idStr = String(prev.id);
      for (const cat of parsedData.categories) {
        const found = (cat.cards || []).find((c) => String((c as CardType).id) === idStr) as
          | CardType
          | undefined;
        if (found && programCards2ModalCardSyncSignature(found) !== programCards2ModalCardSyncSignature(prev)) {
          return found;
        }
      }
      return prev;
    });
  }, [parsedData]);

  if (!parsedResult.success || !parsedData) {
    console.error("ProgramCards2 validation failed", parsedResult.success ? undefined : parsedResult.error);
    return null;
  }

  const isAnimationEnabled = Boolean(parsedData.enable_animation);

  const selectedCategory =
    visibleCategories[selectedCategoryIndex] ?? visibleCategories[0];
  const displayedCards = isDefaultCategory(selectedCategory as any)
    ? categories.flatMap((c) => c.cards || []).sort((a, b) => (a.priority || 0) - (b.priority || 0))
    : (selectedCategory?.cards || []);
  const cardCount = displayedCards.length;

  const scrollMobileToIndex = useCallback((index: number) => {
    const container = mobileScrollRef.current;
    const el = mobileCardRefsRef.current[index];
    if (!container || !el) return;
    mobileScrollSyncLockRef.current = true;
    const targetLeft = el.offsetLeft - (container.clientWidth - el.clientWidth) / 2;
    container.scrollTo({ left: Math.max(0, targetLeft), behavior: "smooth" });
    window.setTimeout(() => {
      mobileScrollSyncLockRef.current = false;
    }, 450);
  }, []);

  const onMobileScroll = useCallback(() => {
    if (mobileScrollSyncLockRef.current) return;
    if (mobileScrollRafRef.current != null) cancelAnimationFrame(mobileScrollRafRef.current);
    mobileScrollRafRef.current = requestAnimationFrame(() => {
      mobileScrollRafRef.current = null;
      const container = mobileScrollRef.current;
      if (!container || cardCount <= 0) return;
      const mid = container.scrollLeft + container.clientWidth / 2;
      let bestIdx = 0;
      let bestDist = Infinity;
      for (let i = 0; i < cardCount; i++) {
        const cardEl = mobileCardRefsRef.current[i];
        if (!cardEl) continue;
        const cardMid = cardEl.offsetLeft + cardEl.clientWidth / 2;
        const d = Math.abs(mid - cardMid);
        if (d < bestDist) {
          bestDist = d;
          bestIdx = i;
        }
      }
      setCurrentCardIndex((prev) => (prev !== bestIdx ? bestIdx : prev));
    });
  }, [cardCount]);

  useEffect(() => {
    setDesktopShowAllGrid(false);
    setCurrentCardIndex(0);
    requestAnimationFrame(() => {
      mobileScrollRef.current?.scrollTo({ left: 0, behavior: "auto" });
    });
  }, [selectedCategoryIndex]);

  useEffect(
    () => () => {
      if (mobileScrollRafRef.current != null) cancelAnimationFrame(mobileScrollRafRef.current);
    },
    [],
  );

  const handlePrev = useCallback(() => {
    setCurrentCardIndex((prev) => {
      const next = Math.max(0, prev - 1);
      queueMicrotask(() => scrollMobileToIndex(next));
      return next;
    });
  }, [scrollMobileToIndex]);

  const handleNext = useCallback(() => {
    setCurrentCardIndex((prev) => {
      const next = Math.min(cardCount - 1, prev + 1);
      queueMicrotask(() => scrollMobileToIndex(next));
      return next;
    });
  }, [cardCount, scrollMobileToIndex]);

  const bgColor = formatBackgroundStyle(parsedData, isMobile ?? false, "#FFFFFF");

  const sectionModalCtaFallback = parsedData.modal_cta_text || "Apply Now";

  const handleModalFooterCtaClick = useCallback(() => {
    const label =
      detailModalCard?.modal_cta_text?.trim() || sectionModalCtaFallback;
    setDetailModalCard(null);
    ctaClick({
      section: "program_cards_2",
      component: "modal_apply_cta",
      label: String(label),
      step: "1",
      entity: "program_cards_2",
      parentEntity: "program_cards_2",
    });
    dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
  }, [dispatch, detailModalCard, sectionModalCtaFallback]);

  const handleCardBrochureClick = useCallback((card: CardType) => {
    const link = card.brochure_link?.trim();
    if (!link) return;
    ctaClick({
      section: "program_cards_2",
      component: "brochure_download",
      label: String(
        card.brochure_button_text?.trim() ||
          card.title ||
          "Brochure",
      ),
      step: "",
      entity: "brochure_download",
      parentEntity: "program_cards_2",
      landing_url: link,
    });
    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.DOWNLOAD,
        downloadLink: link,
      }),
    );
  }, [dispatch]);

  const renderCards = useCallback((cards: CardType[]) => {
    const mod = Math.floor(cards.length / 3);

    return (
      <>
        <div className="hidden w-full grid-cols-1 justify-items-stretch gap-[20px] sm:grid sm:grid-cols-3">
          {
            cards.slice(0, 3 * mod).map((card, i) => (
              <div
                key={(card as any).id ?? i}
                className={PROGRAM_CARDS_2_DESKTOP_CARD_GRID_CELL}
              >
                <ProgramCard
                  card={card as CardType}
                  sectionShowFeatures={parsedData.show_features}
                  enableAnimation={isAnimationEnabled}
                  fonts={fonts}
                  onExploreClick={() => openCardModal(card as CardType)}
                  onBrochureClick={() =>
                    handleCardBrochureClick(card as CardType)
                  }
                />
              </div>
            ))
          }
        </div>

        <div className="hidden sm:flex w-full flex-row justify-center gap-[20px]">
        {
            cards.slice(3 * mod).map((card, i) => (
              <div
                key={(card as any).id ?? i}
                className={cn(
                  PROGRAM_CARDS_2_DESKTOP_CARD_GRID_CELL,
                  "max-w-[307px] md:max-w-[343px] lg:max-w-[410px] xl:max-w-[477px]"
                )}
              >
                <ProgramCard
                  card={card as CardType}
                  sectionShowFeatures={parsedData.show_features}
                  enableAnimation={isAnimationEnabled}
                  fonts={fonts}
                  onExploreClick={() => openCardModal(card as CardType)}
                  onBrochureClick={() =>
                    handleCardBrochureClick(card as CardType)
                  }
                />
              </div>
            ))
          }
        </div>
      </>
    )
  }, []);

  return (
    <section
      ref={sectionRef}
      className={cn(
        "relative w-full overflow-x-hidden flex flex-col items-center",
        "xs:px-[24px] xs:py-[40px]",
        "sm:px-[60px] sm:py-[40px]",
        "md:px-[80px] md:py-[60px]",
        "lg:px-[80px] lg:py-[60px]",
        "xl:px-[140px] xl:py-[80px]",
      )}
      style={bgColor}
    >
      <div
        className={cn(
          "relative z-10 flex w-full flex-col gap-[24px] sm:gap-[40px]",
          "max-w-[345px] mx-auto sm:mx-0 sm:max-w-none",
          "w-full sm:w-[960px]",
          "md:w-[1070px]",
          "lg:w-[1280px]",
          "xl:w-[1470px]",
        )}
      >
        {/* Headings + categories: sm+ same row, titles left / categories right, single line (no wrap), scroll if needed; mobile — categories row, horizontal scroll */}
        <div className="flex w-full flex-col gap-4">
          <div className="flex w-full min-w-0 flex-col gap-[4px] sm:flex-row sm:items-end sm:justify-between sm:gap-6">
            {/* pre_title + title are one <h2>. Spans throughout (a heading may only hold
                phrasing content); `block` restores the line break the <p>s gave. */}
            <h2 className="flex min-w-0 flex-col gap-[4px] sm:gap-[2px]">
              <RevealOnScroll as="span" enableAnimation={isAnimationEnabled}>
                <span
                  className={cn(
                    "block font-semibold leading-normal",
                    fonts.sectionPreTitleClassName,
                  )}
                  style={{
                    color: parsedData.pre_title_color,
                    fontFamily: fonts.preTitle,
                  }}
                >
                  {parsedData.pre_title}
                </span>
              </RevealOnScroll>

              <RevealOnScroll as="span" enableAnimation={isAnimationEnabled}>
                <span
                  className={cn(
                    "block font-bold leading-[1.2] bg-clip-text text-transparent",
                    fonts.sectionTitleClassName,
                  )}
                  style={{
                    backgroundImage:
                      parsedData.title_gradient_angle?.trim() &&
                      parsedData.title_start_color?.trim() &&
                      parsedData.title_end_color?.trim()
                        ? `linear-gradient(${parsedData.title_gradient_angle.trim()}, ${parsedData.title_start_color.trim()}, ${parsedData.title_end_color.trim()})`
                        : undefined,
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    fontFamily: fonts.primaryHeading,
                  }}
                >
                  {parsedData.title}
                </span>
              </RevealOnScroll>
            </h2>
            {showCategoryTabs && (
              <RevealOnScroll enableAnimation={isAnimationEnabled}>

              <div
                className={cn(
                  "hidden min-w-0 flex-1 sm:flex sm:flex-nowrap sm:items-end sm:justify-end sm:gap-3 sm:overflow-x-auto sm:pb-1 sm:[-webkit-overflow-scrolling:touch]",
                  "md:gap-3",
                  "scrollbar-hide",
                )}
              >
                {visibleCategories.map((cat, idx) => {
                  const isActive = selectedCategoryIndex === idx;
                  return (
                    <button
                      key={cat.id ?? idx}
                      type="button"
                      data-cta-name="category_tab"
                      onClick={() => {
                        setSelectedCategoryIndex(idx);
                        setCurrentCardIndex(0);
                      }}
                      className={cn(
                        "shrink-0 rounded-[40px] px-4 py-2.5 text-[14px] md:text-[16px] font-medium leading-[1.49] border transition-colors",
                        isActive ? "" : "border-black/40",
                      )}
                      style={{
                        color: isActive
                          ? (cat.active_text_color ?? "#FFFFFF")
                          : (cat.text_color ?? "#000000"),
                        backgroundColor: isActive
                          ? (cat.active_background_color ?? "#02A7B6")
                          : (cat.background_color ?? "transparent"),
                        borderColor: !isActive ? cat.border_color : undefined,
                        fontFamily: fonts.secondaryHeading,
                      }}
                    >
                      {cat.name}
                    </button>
                  );
                })}
              </div>
              </RevealOnScroll>
            )}
          </div>
          {showCategoryTabs && (
            <RevealOnScroll enableAnimation={isAnimationEnabled}>
            <div
              className={cn(
                "flex w-full min-w-0 flex-nowrap items-center gap-3 overflow-x-auto pb-1 pl-0 pr-1 [-webkit-overflow-scrolling:touch]",
                "scrollbar-hide sm:hidden",
              )}
            >
              {visibleCategories.map((cat, idx) => {
                const isActive = selectedCategoryIndex === idx;
                return (
                  <button
                    key={cat.id ?? idx}
                    type="button"
                    data-cta-name="category_tab"
                    onClick={() => {
                      setSelectedCategoryIndex(idx);
                      setCurrentCardIndex(0);
                    }}
                    className={cn(
                      "inline-flex shrink-0 rounded-[40px] px-4 py-2.5 text-center text-[14px] font-medium leading-[1.49] border transition-colors",
                      isActive ? "border-[#02A7B6]" : "border-black/40",
                    )}
                    style={{
                      color: isActive
                        ? (cat.active_text_color ?? "#FFFFFF")
                        : (cat.text_color ?? "#000000"),
                      backgroundColor: isActive
                        ? (cat.active_background_color ?? "#02A7B6")
                        : (cat.background_color ?? "transparent"),
                      borderColor: !isActive ? cat.border_color : undefined,
                      fontFamily: fonts.secondaryHeading,
                    }}
                  >
                    {cat.name}
                  </button>
                );
              })}
            </div>
            </RevealOnScroll>
          )}
        </div>

        {/* Cards */}
        {displayedCards.length > 0 && (
          <>
            {/* Mobile: horizontal scroll (Figma 3583:10798); Figma 915:12787 — ~40px below category row (24px column gap + mt-4) */}
            <div className="flex w-full flex-col sm:hidden">
              <div className="mx-auto w-full max-w-[345px] shrink-0">
                <div
                  ref={mobileScrollRef}
                  onScroll={onMobileScroll}
                  className={cn(
                    "flex w-full gap-[20px] overflow-x-auto overscroll-x-contain scroll-smooth pb-1",
                    "snap-x snap-mandatory [-webkit-overflow-scrolling:touch]",
                    "scrollbar-hide",
                  )}
                >
                  {displayedCards.map((card, i) => (
                    <div
                      key={(card as any).id ?? i}
                      ref={(el) => {
                        mobileCardRefsRef.current[i] = el;
                      }}
                      className="w-[345px] max-w-[min(345px,100%)] shrink-0 snap-center [scroll-snap-stop:always]"
                    >
                      <ProgramCard
                        card={card as CardType}
                        sectionShowFeatures={parsedData.show_features}
                        enableAnimation={isAnimationEnabled}
                        fonts={fonts}
                        onExploreClick={() => openCardModal(card as CardType)}
                        onBrochureClick={() =>
                          handleCardBrochureClick(card as CardType)
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <MobileProgramsCarouselSlider
              className="sm:hidden"
              currentIndex={currentCardIndex}
              totalCount={displayedCards.length}
              onPrev={handlePrev}
              onNext={handleNext}
            />

            {renderCards(desktopShowAllGrid ? displayedCards : displayedCards.slice(0, 3))}
          </>
        )}

        {/* Section CTA: sm+ only when more than 3 programs (collapsed grid); hidden on mobile — carousel/slider is enough. */}
        {parsedData.cta_text && (
          <>
            {cardCount > 3 && !desktopShowAllGrid ? (
              <div className="hidden w-full justify-center sm:flex">
                {parsedData.cta_button_variant === "variant_1" ? (
                  <Button
                    type="button"
                    data-cta-name="view_all"
                    classType="ghost"
                    className={PROGRAM_CARDS_2_CTA_WMC_BUTTON_CLASSNAME}
                    style={{
                      backgroundColor:
                        parsedData.cta_background_start_color || "#02A7B6",
                      color: parsedData.cta_text_color,
                      fontFamily: fonts.cta,
                    }}
                    onClick={() => setDesktopShowAllGrid(true)}
                  >
                    {parsedData.cta_text}
                    {parsedData.cta_icon?.trim() ? (
                      <div
                        className={
                          PROGRAM_CARDS_2_CTA_WMC_ICON_WRAPPER_CLASSNAME
                        }
                      >
                        <Image
                          src={parsedData.cta_icon.trim()}
                          alt={parsedData.cta_icon_alt?.trim() || ""}
                          fill
                          sizes="36px"
                          className="object-contain"
                        />
                      </div>
                    ) : null}
                  </Button>
                ) : (
                  <button
                    type="button"
                    data-cta-name="view_all"
                    className={cn(
                      "inline-flex max-w-full shrink-0 cursor-pointer items-center justify-center rounded-none border-0 font-semibold leading-[1.49] whitespace-nowrap outline-none ring-0",
                      "box-border gap-[10px] px-[24px] py-[12px] text-[14px] md:text-[16px]",
                      "focus-visible:ring-2 focus-visible:ring-[#342b7c] focus-visible:ring-offset-2",
                    )}
                    style={{
                      backgroundImage: `linear-gradient(${parsedData.cta_background_angle?.trim() || PROGRAM_CARDS_2_MAIN_CTA_DEFAULTS.cta_background_angle}, ${parsedData.cta_background_start_color?.trim() || PROGRAM_CARDS_2_MAIN_CTA_DEFAULTS.cta_background_start_color}, ${parsedData.cta_background_end_color?.trim() || PROGRAM_CARDS_2_MAIN_CTA_DEFAULTS.cta_background_end_color})`,
                      color: parsedData.cta_text_color,
                      fontFamily: fonts.cta,
                    }}
                    onClick={() => setDesktopShowAllGrid(true)}
                  >
                    {parsedData.cta_text}
                    {parsedData.cta_icon?.trim() ? (
                      <Image
                        src={parsedData.cta_icon.trim()}
                        alt={parsedData.cta_icon_alt?.trim() || ""}
                        width={18}
                        height={18}
                        className="size-[18px] shrink-0 object-contain"
                      />
                    ) : (
                      <CtaTrailingArrow />
                    )}
                  </button>
                )}
              </div>
            ) : null}
          </>
        )}
      </div>

      <ProgramCardDetailModal
        card={detailModalCard}
        open={!!detailModalCard}
        onOpenChange={(next) => {
          if (!next) setDetailModalCard(null);
        }}
        footerCtaText={sectionModalCtaFallback}
        footerCtaStyle={{
          angle:
            parsedData.cta_background_angle?.trim() ||
            PROGRAM_CARDS_2_MAIN_CTA_DEFAULTS.cta_background_angle,
          start:
            parsedData.cta_background_start_color?.trim() ||
            PROGRAM_CARDS_2_MAIN_CTA_DEFAULTS.cta_background_start_color,
          end:
            parsedData.cta_background_end_color?.trim() ||
            PROGRAM_CARDS_2_MAIN_CTA_DEFAULTS.cta_background_end_color,
          color:
            parsedData.cta_text_color?.trim() ||
            PROGRAM_CARDS_2_MAIN_CTA_DEFAULTS.cta_text_color,
        }}
        ctaIconUrl={parsedData.cta_icon}
        ctaIconAlt={parsedData.cta_icon_alt}
        ctaButtonVariant={
          parsedData.cta_button_variant === "variant_1"
            ? "variant_1"
            : "variant_2"
        }
        fonts={fonts}
        onFooterCtaClick={handleModalFooterCtaClick}
      />
    </section>
  );
};

export { MobileProgramsCarouselSlider, ProgramCardDetailModal, ProgramCard };
